
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface ChangeRequestSectionProps {
  onRequestChange: (requestType: "reassign" | "extend", details: string) => void;
}

const ChangeRequestSection = ({
  onRequestChange,
}: ChangeRequestSectionProps) => {
  const [extensionDetails, setExtensionDetails] = useState("");
  const [reassignmentDetails, setReassignmentDetails] = useState("");
  const [showExtendPopover, setShowExtendPopover] = useState(false);
  const [showReassignPopover, setShowReassignPopover] = useState(false);

  const handleExtendDeadline = () => {
    if (extensionDetails.trim()) {
      onRequestChange("extend", extensionDetails);
      setExtensionDetails("");
      setShowExtendPopover(false);
    }
  };

  const handleRequestReassignment = () => {
    if (reassignmentDetails.trim()) {
      onRequestChange("reassign", reassignmentDetails);
      setReassignmentDetails("");
      setShowReassignPopover(false);
    }
  };

  return (
    <div className="border-t pt-4">
      <div className="flex justify-between items-center mb-2">
        <h4 className="text-sm font-medium">Request Changes</h4>
      </div>
      <div className="flex gap-2">
        <Popover open={showReassignPopover} onOpenChange={setShowReassignPopover}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              Request Reassignment
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-3">
              <h4 className="font-medium">Request Task Reassignment</h4>
              <div className="space-y-2">
                <Label htmlFor="reassign-details">Reason for reassignment</Label>
                <Textarea 
                  id="reassign-details"
                  placeholder="Please explain why this task needs to be reassigned"
                  value={reassignmentDetails}
                  onChange={(e) => setReassignmentDetails(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowReassignPopover(false)}
                >
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleRequestReassignment}
                  disabled={!reassignmentDetails.trim()}
                >
                  Submit Request
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Popover open={showExtendPopover} onOpenChange={setShowExtendPopover}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              Request Deadline Extension
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-3">
              <h4 className="font-medium">Request Deadline Extension</h4>
              <div className="space-y-2">
                <Label htmlFor="extend-details">Extension details</Label>
                <Textarea 
                  id="extend-details"
                  placeholder="Please explain why you need more time and propose a new deadline"
                  value={extensionDetails}
                  onChange={(e) => setExtensionDetails(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowExtendPopover(false)}
                >
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleExtendDeadline}
                  disabled={!extensionDetails.trim()}
                >
                  Submit Request
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default ChangeRequestSection;
